import { fetchPrivateFile, privateObjectUrl, revokePrivateObjectUrl } from "./privateFiles";
export type DownloadableFile = { filename: string; mediaType: string; url: string | null };

// Imported files sometimes have an Office filename but contain an image.
// Identify the bytes before choosing the preview and saved filename.
export async function inspectFile(blob: Blob, filename: string, mediaType: string) {
  const bytes = new Uint8Array(await blob.slice(0, 12).arrayBuffer());
  const starts = (...prefix: number[]) => prefix.every((value, index) => bytes[index] === value);
  let suffix: string | undefined;
  let type = mediaType || blob.type || "application/octet-stream";
  if (starts(137, 80, 78, 71, 13, 10, 26, 10)) { suffix = "png"; type = "image/png"; }
  else if (starts(255, 216, 255)) { suffix = "jpg"; type = "image/jpeg"; }
  else if (starts(37, 80, 68, 70, 45)) { suffix = "pdf"; type = "application/pdf"; }
  else if (starts(98, 112, 108, 105, 115, 116)) { suffix = "plist"; type = "application/octet-stream"; }
  const safeName = filename.replace(/./gs, char => char.charCodeAt(0) < 32 || char === "/" || char === "\\" ? "_" : char);
  const name = suffix && !safeName.toLowerCase().endsWith(`.${suffix}`)
    ? `${safeName.replace(/\.[^.]+$/, "")}.${suffix}` : safeName;
  return { filename: name, mediaType: type, blob: new Blob([blob], { type }) };
}

export async function fetchFile(file: DownloadableFile, signal?: AbortSignal) {
  if (!file.url) throw new Error("This file is unavailable.");
  return await inspectFile(await fetchPrivateFile(file.url, signal), file.filename, file.mediaType);
}

export async function downloadFile(file: DownloadableFile) {
  const resolved = await fetchFile(file);
  const url = privateObjectUrl(resolved.blob);
  const anchor = document.createElement("a");
  anchor.href = url;
  anchor.download = resolved.filename;
  document.body.append(anchor);
  anchor.click();
  anchor.remove();
  // Allow Safari time to start reading the object URL.
  window.setTimeout(() => revokePrivateObjectUrl(url), 60_000);
}

export function isAppleMobileBrowser() {
  return /iPad|iPhone|iPod/.test(navigator.userAgent) ||
    (navigator.platform === "MacIntel" && navigator.maxTouchPoints > 1);
}

export async function sharePreparedFile(file: File): Promise<void> {
  if (typeof navigator.share !== "function" || typeof navigator.canShare !== "function" || !navigator.canShare({ files: [file] })) {
    throw new Error("This browser cannot save this file here. Open Yodl in Safari, then use Save file.");
  }
  try {
    await navigator.share({ files: [file] });
  } catch (caught) {
    if (caught instanceof Error && caught.name === "AbortError") return;
    throw new Error("The save menu could not open. Tap Save file again, then choose Save to Files.");
  }
}
